import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';

const CalendarForm = ({ isEdit, formData, setFormData, availableHolidays, loadAvailableHolidays, isSubmitting, handleSubmit, onClose }) => {
  const { t, i18n } = useTranslation();

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
    <div className="space-y-2">
      <Label htmlFor="name">{t("holidayCalendarComponents.form.name")}</Label>
      <Input
        id="name"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        placeholder={t("holidayCalendarComponents.form.namePlaceholder")}
        required
      />
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="year">{t("holidayCommon.fields.year")} *</Label>
        <Input
          id="year"
          type="number"
          value={formData.year}
          onChange={(e) => {
            const year = parseInt(e.target.value);
            setFormData({ ...formData, year, holiday_ids: [] });
            loadAvailableHolidays(year, formData.country);
          }}
          min="2020"
          max="2050"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="country">{t("holidayCommon.fields.country")} *</Label>
        <Input
          id="country"
          value={formData.country}
          onChange={(e) => {
            const country = e.target.value;
            setFormData({ ...formData, country, holiday_ids: [] });
            loadAvailableHolidays(formData.year, country);
          }}
          placeholder={t("holidayCalendarComponents.form.countryPlaceholder")}
          required
        />
      </div>
    </div>

    <div className="flex items-center space-x-2">
      <Checkbox
        id="is_template"
        checked={formData.is_template}
        onCheckedChange={(checked) => setFormData({ ...formData, is_template: checked })}
      />
      <Label htmlFor="is_template" className="cursor-pointer">
        {t("holidayCalendarComponents.form.useAsTemplate")}
      </Label>
    </div>

    {!isEdit && availableHolidays.length > 0 && (
      <div className="space-y-2">
        <Label>{t("holidayCalendarComponents.form.holidaysToAdd")}</Label>
        <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
          {availableHolidays.map((holiday) => (
            <div key={holiday.id} className="flex items-center space-x-2">
              <Checkbox
                id={`holiday-${holiday.id}`}
                checked={formData.holiday_ids.includes(holiday.id)}
                onCheckedChange={(checked) => {
                  setFormData({
                    ...formData,
                    holiday_ids: checked
                      ? [...formData.holiday_ids, holiday.id]
                      : formData.holiday_ids.filter((id) => id !== holiday.id),
                  });
                }}
              />
              <Label htmlFor={`holiday-${holiday.id}`} className="cursor-pointer text-sm">
                {holiday.name} – {new Date(holiday.date).toLocaleDateString(i18n.language)}
              </Label>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {t("holidayCalendarComponents.form.selectedHolidays", { count: formData.holiday_ids.length })}
        </p>
      </div>
    )}

    <DialogFooter>
      <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
        {t("holidayCommon.cancel")}
      </Button>
      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? t("holidayCalendarComponents.form.submitting") : isEdit ? t("holidayCalendarComponents.form.updateCta") : t("holidayCalendarComponents.form.createCta")}
      </Button>
    </DialogFooter>
    </form>
  );
};

const CalendriersFormDialog = ({
  isCreateModalOpen, setIsCreateModalOpen,
  isEditModalOpen, setIsEditModalOpen,
  formData, setFormData,
  availableHolidays, loadAvailableHolidays,
  isSubmitting,
  handleCreateCalendar, handleUpdateCalendar,
}) => {
  const { t } = useTranslation();

  return (
    <>
    <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("holidayCalendarComponents.form.createTitle")}</DialogTitle>
          <DialogDescription>
            {t("holidayCalendarComponents.form.createDesc")}
          </DialogDescription>
        </DialogHeader>
        <CalendarForm
          isEdit={false}
          formData={formData}
          setFormData={setFormData}
          availableHolidays={availableHolidays}
          loadAvailableHolidays={loadAvailableHolidays}
          isSubmitting={isSubmitting}
          handleSubmit={handleCreateCalendar}
          onClose={() => setIsCreateModalOpen(false)}
        />
      </DialogContent>
    </Dialog>

    <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("holidayCalendarComponents.form.editTitle")}</DialogTitle>
          <DialogDescription>{t("holidayCalendarComponents.form.editDesc")}</DialogDescription>
        </DialogHeader>
        <CalendarForm
          isEdit
          formData={formData}
          setFormData={setFormData}
          availableHolidays={availableHolidays}
          loadAvailableHolidays={loadAvailableHolidays}
          isSubmitting={isSubmitting}
          handleSubmit={handleUpdateCalendar}
          onClose={() => setIsEditModalOpen(false)}
        />
      </DialogContent>
    </Dialog>
    </>
  );
};

export default CalendriersFormDialog;
